// Presentation helpers ported from the Claude Design mockup, adapted to the
// LOCKED schema: status keys are todo|in_progress|done and due dates are real
// Date objects (converted from Firestore Timestamps at the data boundary).

// Default statuses — fallback when the `statuses` collection is empty. The team
// can override/extend these via Settings; the three ids stay stable for
// back-compat with existing/seeded data and `setTaskStatus`.
export const DEFAULT_STATUSES = [
  { id: 'todo', name: 'To Do', dot: '#7A7568', color: '#7A7568', order: 0, done: false },
  { id: 'in_progress', name: 'In Progress', dot: '#C8A84B', color: '#C8A84B', order: 1, done: false },
  { id: 'done', name: 'Done', dot: '#3DB89A', color: '#3DB89A', order: 2, done: true },
]


/**
 * The three kinds of engagement. Replaces the old `board` field, whose two values
 * were a UI word for the same idea. Stored on `engagements.type`; read everywhere
 * through engagementType() so a not-yet-migrated doc still resolves.
 */
export const ENGAGEMENT_TYPE_OPTIONS = [
  { value: 'advisory', label: 'Advisory', color: '#4A7CC9' },
  { value: 'speaking', label: 'Speaking', color: '#C8A84B' },
  { value: 'pr', label: 'PR & Media', color: '#3DB89A' },
]

export const ENGAGEMENT_TYPES = ENGAGEMENT_TYPE_OPTIONS.map((o) => o.value)

/**
 * Canonical type of an engagement — its `type`, defaulting to speaking. The
 * legacy `board` fallback was dropped once the Wave 2 migration ran in prod
 * (2026-10-11) and no `board`-only docs remained.
 */
export function engagementType(e) {
  const t = (e && e.type) || 'speaking'
  return ENGAGEMENT_TYPES.includes(t) ? t : 'speaking'
}

/** Display label for an engagement type. */
export function engagementTypeLabel(type) {
  const o = ENGAGEMENT_TYPE_OPTIONS.find((x) => x.value === type)
  return o ? o.label : 'Speaking'
}

export const FEE_STATUS_OPTIONS = [
  { value: 'offered', label: 'Offered', color: '#C8A84B' },
  { value: 'confirmed', label: 'Confirmed', color: '#4A7CC9' },
  { value: 'paid', label: 'Paid', color: '#3DB89A' },
]

/**
 * The one status set, seeded per engagement type (register item 12). Kaycia asked
 * for submitted / pending / confirmed / past; `declined` is a deliberate fifth —
 * her four have no lost state, and the weighted forecast needs one to stay honest.
 * FLAG THIS TO HER.
 *
 * `probability` and `outcome` moved here from the deleted `dealStages`; they are
 * what let pipelineForecast()/dealMetrics() weight a pipeline. Stages stay editable
 * in-app, so these are seeds, not a locked enum.
 */
export const ENGAGEMENT_STAGE_TEMPLATE = [
  { slug: 'submitted', name: 'Submitted', dot: '#7A7568', color: '#7A7568', order: 0, done: false, probability: 10, outcome: null },
  { slug: 'pending', name: 'Pending', dot: '#C8A84B', color: '#C8A84B', order: 1, done: false, probability: 40, outcome: null },
  { slug: 'confirmed', name: 'Confirmed', dot: '#4A7CC9', color: '#4A7CC9', order: 2, done: false, probability: 90, outcome: null },
  { slug: 'past', name: 'Past', dot: '#3DB89A', color: '#3DB89A', order: 3, done: true, probability: 100, outcome: 'won' },
  { slug: 'declined', name: 'Declined', dot: 'var(--color-danger)', color: 'var(--color-danger)', order: 4, done: true, probability: 0, outcome: 'lost' },
]

export const engagementStageId = (type, slug) => `eng-stage-${type}-${slug}`

export const DEFAULT_ENGAGEMENT_STAGES = ENGAGEMENT_TYPES.flatMap((type) =>
  ENGAGEMENT_STAGE_TEMPLATE.map(({ slug, ...s }) => ({ id: engagementStageId(type, slug), type, ...s })),
)

/**
 * Legacy stage id -> new stage id. Applied by scripts/migrate-engagements.mjs and
 * pinned by scripts/utest-migrate.mjs. Two collapses lose a distinction the old
 * boards made and are called out in the Wave 2 spec: Negotiating folds into
 * Pending, Follow-up folds into Past.
 */
export const LEGACY_STAGE_REMAP = {
  'eng-stage-speaking-lead': 'eng-stage-speaking-submitted',
  'eng-stage-speaking-pitched': 'eng-stage-speaking-pending',
  'eng-stage-speaking-negotiating': 'eng-stage-speaking-pending',
  'eng-stage-speaking-confirmed': 'eng-stage-speaking-confirmed',
  'eng-stage-speaking-delivered': 'eng-stage-speaking-past',
  'eng-stage-speaking-followup': 'eng-stage-speaking-past',
  'eng-stage-pr-target': 'eng-stage-pr-submitted',
  'eng-stage-pr-pitched': 'eng-stage-pr-pending',
  'eng-stage-pr-interviewing': 'eng-stage-pr-pending',
  'eng-stage-pr-placed': 'eng-stage-pr-confirmed',
  'eng-stage-pr-published': 'eng-stage-pr-past',
}

/** Which task project backs each engagement type's action items. */
export const ENGAGEMENT_BOARD_PROJECT = { advisory: 'cat-advisory', speaking: 'cat-speaking', pr: 'cat-pr' }

/** Revenue amount of a pipeline record (an engagement's `fee`), 0 when unset. */
export const recordAmount = (r) => {
  const v = r && r.fee
  return typeof v === 'number' && !Number.isNaN(v) ? v : 0
}

/** Stage id of a pipeline record (an engagement's `stage`). */
export const recordStageId = (r) => (r && r.stage) || null

/** Raw and probability-weighted forecast per stage and overall. Pure. */
export function pipelineForecast(records = [], stages = []) {
  const probById = {}
  stages.forEach((s) => { probById[s.id] = typeof s.probability === 'number' ? s.probability : 0 })
  const byStage = {}
  stages.forEach((s) => { byStage[s.id] = { count: 0, raw: 0, weighted: 0 } })
  let totalRaw = 0, totalWeighted = 0
  records.forEach((d) => {
    const amt = recordAmount(d)
    const sid = recordStageId(d)
    const w = amt * ((probById[sid] || 0) / 100)
    if (!byStage[sid]) byStage[sid] = { count: 0, raw: 0, weighted: 0 }
    byStage[sid].count += 1
    byStage[sid].raw += amt
    byStage[sid].weighted += w
    totalRaw += amt
    totalWeighted += w
  })
  return { byStage, totalRaw, totalWeighted }
}

export function statusLabel(id, statuses = DEFAULT_STATUSES) {
  return (statuses.find((s) => s.id === id) || {}).name || id
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/**
 * Local midnight of `dt`. "Today" means the viewer's calendar day, not UTC's.
 * Every day-boundary question in the app resolves through this one function so
 * that a dashboard count, a list grouping and a row's own date chip can never
 * disagree. Lives here rather than in metrics.js because metrics.js imports
 * from this module — the dependency has to point this way.
 */
export const startOfDay = (dt) => { const d = new Date(dt); d.setHours(0, 0, 0, 0); return d }

/** Whole calendar days from `today` to `date`. Negative means in the past. */
export const dayDelta = (date, today) => Math.round((startOfDay(date).getTime() - startOfDay(today).getTime()) / 86400000)

/** Format a Date as "Jun 26". Returns null for nullish input. */
export function fmtDate(date) {
  if (!date) return null
  return `${MONTHS[date.getMonth()]} ${date.getDate()}`
}

/** Format a number as "$5,000". Returns '' for nullish input. */
export function fmtMoney(n) {
  if (typeof n !== 'number' || Number.isNaN(n)) return ''
  return '$' + n.toLocaleString('en-US')
}

/** Due-date metadata: label, overdue/soon flags, reminder text. */
export function dueMeta(date, done) {
  if (!date) return { label: null, hasDue: false, overdue: false, soon: false, reminderText: '' }
  const diff = dayDelta(date, new Date())
  const overdue = !done && diff < 0
  const soon = !done && diff >= 0 && diff <= 2
  let reminderText = ''
  if (overdue) reminderText = diff === -1 ? 'Overdue by 1 day' : 'Overdue'
  else if (soon) reminderText = diff === 0 ? 'Due today' : diff === 1 ? 'Due tomorrow' : 'Due soon'
  return { label: fmtDate(date), hasDue: true, overdue, soon, reminderText }
}

/** Reminder indicator (the gold/red diamond) shown for soon/overdue tasks. */
export function bellMeta(meta, done) {
  if (done) return { show: false }
  if (meta.overdue)
    return { show: true, glyph: '◆', style: { color: 'var(--color-danger)', fontSize: 10, flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 5 } }
  if (meta.soon)
    return { show: true, glyph: '◆', style: { color: 'var(--color-gold)', fontSize: 10, flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 5 } }
  return { show: false }
}

/** Round avatar style — color/ink come from the user's profile doc. */
export function avatarStyle(color, ink, size) {
  return {
    width: size,
    height: size,
    borderRadius: '50%',
    background: color,
    color: ink,
    fontSize: Math.round(size * 0.4),
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    letterSpacing: '.02em',
    fontFamily: 'var(--font-sans)',
    border: 'none',
    cursor: 'default',
  }
}

/** Outlined label chip in the label's color. */
export function chipStyle(color) {
  return {
    fontFamily: 'var(--font-mono)',
    fontSize: 10,
    textTransform: 'uppercase',
    letterSpacing: '.05em',
    color,
    border: '1px solid ' + color,
    borderRadius: 'var(--radius-sm)',
    padding: '1px 6px',
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    alignItems: 'center',
    background: 'transparent',
  }
}

/** Initials from a display name, e.g. "Maya Patel" -> "MP". */
export function initialsOf(name) {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/)
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || name[0].toUpperCase()
}

// Deterministic avatar palette so each person (real or invited) gets a stable
// color/ink, picked by hashing a seed (uid for real users, email for invites).
export const AVATAR_PALETTE = [
  { color: '#C8A84B', ink: '#0C0B09' },
  { color: '#3DB89A', ink: '#0C0B09' },
  { color: '#4A7CC9', ink: '#F4EFE6' },
  { color: '#8A6A1E', ink: '#F4EFE6' },
  { color: 'var(--color-danger)', ink: '#0C0B09' },
  { color: '#7A7568', ink: '#F4EFE6' },
]

export function paletteFor(seed) {
  const s = String(seed || '')
  let h = 0
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length]
}

/** Convert a Firestore Timestamp (or null) to a JS Date (or null). */
export function tsToDate(ts) {
  if (!ts) return null
  if (ts instanceof Date) return ts
  if (typeof ts.toDate === 'function') return ts.toDate()
  return null
}

/** Convert "YYYY-MM-DD" (from <input type=date>) to a local Date or null. */
export function dateInputToDate(value) {
  if (!value) return null
  return new Date(value + 'T00:00:00')
}

/**
 * Convert a Date — or a raw Firestore Timestamp, which is what stored records
 * hold — to "YYYY-MM-DD" for <input type=date>.
 */
export function dateToInput(value) {
  const date = tsToDate(value)
  if (!date) return ''
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** Render @Mentions in comment text as gold spans (returns a React-ready array). */
export function splitMentions(text) {
  return text.split(/(@[A-Za-z]+ [A-Za-z]+)/g)
}
export function isMention(part) {
  return /^@[A-Za-z]+ [A-Za-z]+$/.test(part)
}

/**
 * A stage can be deleted only if it is empty and not the last stage on its
 * board/pipeline. `field` names the property on each record that holds its
 * stage id — engagements use `stage` (default, back-compat); deals pass
 * `stageId`.
 */
export function canDeleteStage(stageId, records = [], stages = [], field = 'stage') {
  if (stages.length <= 1) return false
  return !records.some((r) => r[field] === stageId)
}

/**
 * Shared task-filtering helper reused by ListView, BoardView, and later
 * Calendar/Table views. Pure: returns a new filtered array, never mutates
 * `tasks`. Every option in `opts` is optional — omitted/falsy options are
 * no-ops, so callers only pass the filters they support (e.g. BoardView
 * currently omits phaseFilter).
 */
export function filterTasks(tasks, opts = {}) {
  const { labelFilter, assigneeFilter, phaseFilter, incompleteOnly, dueSoonOnly, search, statusesById = {} } = opts
  let result = tasks
  if (labelFilter) result = result.filter((t) => (t.labelIds || []).includes(labelFilter))
  if (assigneeFilter) result = result.filter((t) => t.assigneeUid === assigneeFilter)
  if (phaseFilter) result = result.filter((t) => t.phase === phaseFilter)
  if (search && search.trim()) {
    const q = search.trim().toLowerCase()
    result = result.filter((t) => `${t.title} ${t.description || ''}`.toLowerCase().includes(q))
  }
  if (incompleteOnly) result = result.filter((t) => !statusesById[t.status]?.done)
  if (dueSoonOnly) {
    const now = Date.now(), in14 = now + 14 * 86400000
    result = result.filter((t) => { const d = tsToDate(t.dueDate); return d && d.getTime() >= now - 86400000 && d.getTime() <= in14 })
  }
  return result
}

/**
 * Filter engagements by owner + free-text search (title/org/contact). Pure:
 * returns a new filtered array, never mutates `engagements`. Every option in
 * `opts` is optional — omitted/falsy options are no-ops (passthrough).
 */
export function filterEngagements(engagements = [], opts = {}) {
  const { ownerFilter, search } = opts
  let result = engagements
  if (ownerFilter) result = result.filter((e) => e.ownerUid === ownerFilter)
  if (search && search.trim()) {
    const q = search.trim().toLowerCase()
    result = result.filter((e) => `${e.title || ''} ${e.org || ''} ${e.contactName || ''} ${e.contactOrg || ''}`.toLowerCase().includes(q))
  }
  return result
}

/** Owner + free-text filter for deals; search spans name + primary company name. */

/**
 * Resolve which engagement board to route to when opening an engagement
 * record. Callers may be invoked from any route (not just an engagements
 * board), so `currentBoard` alone is unreliable — it's null off-board.
 * Priority: an explicit board (e.g. passed by the sidebar or an association
 * link) wins; otherwise look up the engagement's own type in the known
 * engagements list; otherwise fall back to the current route's board (valid
 * when already on an engagements board). Pure.
 */
export function pickEngagementBoard(id, explicitBoard, engagements = [], currentBoard) {
  if (explicitBoard) return explicitBoard
  const found = engagements.find((e) => e.id === id)
  return found ? engagementType(found) : currentBoard
}

/** Tally records by a key; skips records whose key value is falsy. Pure. */
export function countBy(records = [], key) {
  const out = {}
  records.forEach((r) => { const v = r[key]; if (v) out[v] = (out[v] || 0) + 1 })
  return out
}

/** Pure: how many in-memory records point at a company (for delete warnings). */
export function countCompanyDependents(companyId, contacts = [], engagements = []) {
  return {
    contacts: contacts.filter((c) => c.primaryCompanyId === companyId).length,
    engagements: engagements.filter((e) => e.primaryCompanyId === companyId).length,
  }
}

/** Fractional order for dropping a card between `before` and `after` orders. */
export function orderBetween(before, after) {
  if (before == null && after == null) return Date.now()
  if (before == null) return after - 1
  if (after == null) return before + 1
  return (before + after) / 2
}

/**
 * Fractional order to place `draggedId` at visual position `destIndex` within a
 * column. `colItems`: that column's items sorted by `.order` (may include the
 * dragged item). `destIndex` null/undefined or >= length -> append to end.
 */
export function orderForIndex(colItems, draggedId, destIndex) {
  const others = colItems.filter((it) => it.id !== draggedId)
  if (destIndex == null || destIndex >= others.length) {
    const last = others[others.length - 1]
    return orderBetween(last ? last.order : null, null)
  }
  const before = destIndex > 0 ? others[destIndex - 1] : null
  const after = others[destIndex]
  return orderBetween(before ? before.order : null, after ? after.order : null)
}

/**
 * Insert index for a pointer at viewport `pointerY`, given `mids` = card vertical
 * midpoints (viewport px) top->bottom, excluding the dragged card.
 */
export function indexFromMidpoints(mids, pointerY) {
  let i = 0
  while (i < mids.length && mids[i] < pointerY) i++
  return i
}

// ── CRM helpers ───────────────────────────────────────────────────────────

/** Normalized contact dedup key: trimmed + lowercased email. '' for nullish. */
export function normalizeEmail(input) {
  if (!input) return ''
  return String(input).trim().toLowerCase()
}

/** Normalized company dedup key: bare host, no scheme/www/path, lowercased. */
export function normalizeDomain(input) {
  if (!input) return ''
  let d = String(input).trim().toLowerCase()
  d = d.replace(/^https?:\/\//, '').replace(/^www\./, '')
  d = d.split('/')[0].split('?')[0]
  return d
}

/** Association endpoint token, e.g. objToken('contact','c1') -> 'contact:c1'. */
export function objToken(type, id) {
  return `${type}:${id}`
}

/** Split a token back into { type, id } on the first ':'. */
export function parseToken(token) {
  const i = String(token).indexOf(':')
  if (i < 0) return { type: token, id: '' }
  return { type: token.slice(0, i), id: token.slice(i + 1) }
}

/**
 * Route a "type:id" token to the matching handler. `handlers` is keyed by token
 * type ({ contact, company, deal, engagement }); the matched handler is called
 * with the id. Unknown types are a no-op. Pure — no React/Firestore.
 */
export function dispatchToken(token, handlers) {
  const { type, id } = parseToken(token)
  const fn = handlers && handlers[type]
  if (fn) fn(id)
}

/** Deterministic association doc id: the two endpoint tokens sorted + joined. */
export function associationPairKey(tokenA, tokenB) {
  return [tokenA, tokenB].sort().join('__')
}

/** Given an association doc and one endpoint, return the other endpoint. */
export function otherEndpoint(assoc, selfToken) {
  const eps = assoc?.endpoints || []
  return eps.find((e) => e !== selfToken) || null
}

/** Allowlisted association pair keys (sorted "typeA:typeB"). Gates the UI, not storage. */
export const ASSOCIATION_TYPES = [
  'company:contact',
  'contact:engagement',
  'company:engagement',
  'contact:task',
  'company:task',
  'engagement:task',
]

export const LIFECYCLE_OPTIONS = [
  { value: 'lead', label: 'Lead', color: '#7A7568' },
  { value: 'qualified', label: 'Qualified', color: '#C8A84B' },
  { value: 'opportunity', label: 'Opportunity', color: '#4A7CC9' },
  { value: 'customer', label: 'Customer', color: '#3DB89A' },
  { value: 'churned', label: 'Churned', color: 'var(--color-danger)' },
]

export const INDUSTRY_OPTIONS = [
  { value: 'nonprofit', label: 'Nonprofit', color: '#3DB89A' },
  { value: 'corporate', label: 'Corporate', color: '#4A7CC9' },
  { value: 'government', label: 'Government', color: '#7A7568' },
  { value: 'education', label: 'Education', color: '#C8A84B' },
  { value: 'media', label: 'Media', color: '#8A6A1E' },
  { value: 'other', label: 'Other', color: '#7A7568' },
]

export const COMPANY_SIZE_OPTIONS = [
  { value: 'sm', label: '1–50', color: '#7A7568' },
  { value: 'md', label: '51–500', color: '#C8A84B' },
  { value: 'lg', label: '500+', color: '#4A7CC9' },
]
/** Color of the option whose value matches, else a neutral default. */
export function optionColor(options = [], value) {
  const o = (options || []).find((opt) => opt.value === value)
  return (o && o.color) || '#7A7568'
}

/**
 * Seed definitions for CORE properties (backed by real typed columns). Rendered
 * through the same <PropertyField> as custom props so the UI is uniform.
 * `group` = which left-column section; `order` = order within the group.
 */
export const CORE_PROPERTY_DEFS = [
  // Contact
  { objectType: 'contact', key: 'firstName', label: 'First name', type: 'text', group: 'About', order: 0, isCore: true, required: false, options: [] },
  { objectType: 'contact', key: 'lastName', label: 'Last name', type: 'text', group: 'About', order: 1, isCore: true, required: false, options: [] },
  { objectType: 'contact', key: 'email', label: 'Email', type: 'text', group: 'About', order: 2, isCore: true, required: false, options: [] },
  { objectType: 'contact', key: 'phone', label: 'Phone', type: 'text', group: 'About', order: 3, isCore: true, required: false, options: [] },
  { objectType: 'contact', key: 'title', label: 'Job title', type: 'text', group: 'About', order: 4, isCore: true, required: false, options: [] },
  { objectType: 'contact', key: 'lifecycleStage', label: 'Lifecycle stage', type: 'select', group: 'About', order: 5, isCore: true, required: false, options: LIFECYCLE_OPTIONS },
  { objectType: 'contact', key: 'ownerUid', label: 'Owner', type: 'user', group: 'About', order: 6, isCore: true, required: false, options: [] },
  // Company
  { objectType: 'company', key: 'name', label: 'Name', type: 'text', group: 'About', order: 0, isCore: true, required: false, options: [] },
  { objectType: 'company', key: 'domain', label: 'Domain', type: 'text', group: 'About', order: 1, isCore: true, required: false, options: [] },
  { objectType: 'company', key: 'industry', label: 'Industry', type: 'select', group: 'About', order: 2, isCore: true, required: false, options: INDUSTRY_OPTIONS },
  { objectType: 'company', key: 'size', label: 'Size', type: 'select', group: 'About', order: 3, isCore: true, required: false, options: COMPANY_SIZE_OPTIONS },
  { objectType: 'company', key: 'city', label: 'City', type: 'text', group: 'About', order: 4, isCore: true, required: false, options: [] },
  { objectType: 'company', key: 'website', label: 'Website', type: 'url', group: 'About', order: 5, isCore: true, required: false, options: [] },
  { objectType: 'company', key: 'ownerUid', label: 'Owner', type: 'user', group: 'About', order: 6, isCore: true, required: false, options: [] },
  // Engagement — the converged opportunity object. `fee` keeps its stored key but
  // is LABELLED "Revenue" (register item 14): propertyDefs separates key from
  // label, so her wording lands with no data migration.
  { objectType: 'engagement', key: 'title', label: 'Title', type: 'text', group: 'About', order: 0, isCore: true, required: false, options: [] },
  { objectType: 'engagement', key: 'org', label: 'Client / org', type: 'text', group: 'About', order: 1, isCore: true, required: false, options: [] },
  { objectType: 'engagement', key: 'type', label: 'Type', type: 'select', group: 'About', order: 2, isCore: true, required: false, options: ENGAGEMENT_TYPE_OPTIONS },
  { objectType: 'engagement', key: 'date', label: 'Date', type: 'date', group: 'About', order: 3, isCore: true, required: false, options: [] },
  { objectType: 'engagement', key: 'endDate', label: 'End date', type: 'date', group: 'About', order: 4, isCore: true, required: false, options: [] },
  { objectType: 'engagement', key: 'location', label: 'Location', type: 'text', group: 'About', order: 5, isCore: true, required: false, options: [] },
  { objectType: 'engagement', key: 'fee', label: 'Revenue', type: 'currency', group: 'About', order: 6, isCore: true, required: false, options: [] },
  { objectType: 'engagement', key: 'feeStatus', label: 'Revenue status', type: 'select', group: 'About', order: 7, isCore: true, required: false, options: FEE_STATUS_OPTIONS },
  { objectType: 'engagement', key: 'offerNotes', label: 'Offer', type: 'text', group: 'About', order: 8, isCore: true, required: false, options: [] },
  { objectType: 'engagement', key: 'ownerUid', label: 'Owner', type: 'user', group: 'About', order: 9, isCore: true, required: false, options: [] },
]
